import React, {useContext, useEffect, useRef, useState} from 'react';
import {Text, View, Pressable, Dimensions, StyleSheet} from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import DataOps from '../../data/DataOps';
import {useNavigate} from 'react-router-dom';
import {GlobalStateContext} from './GlobalStateProvider';
import { resetTelemetry } from '../../utility/telemetry';

const styles = StyleSheet.create({
    dropdown: {
        position: 'absolute',
        top: 87,
        zIndex: 35,
        backgroundColor: 'white',
        width: 300,
        borderRadius: 16,
        shadowColor: '#000',
        shadowOpacity: 0.12,
        shadowRadius: 24,
        shadowOffset: { width: 0, height: 12 },
        elevation: 8,
        borderWidth: 1,
        borderColor: 'rgba(0,0,0,0.06)'
    },
    sectionHeader: {
        paddingHorizontal: 16,
        paddingTop: 10,
        paddingBottom: 6,
        color: '#6B7280',
        fontSize: 12,
        fontWeight: '600',
        letterSpacing: 0.4,
    },
    itemRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 14,
        paddingVertical: 12,
        gap: 12,
    },
    left: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    iconWrap: {
        width: 34,
        height: 34,
        borderRadius: 10,
        backgroundColor: '#F3F4F6',
        alignItems: 'center',
        justifyContent: 'center',
    },
    itemText: { fontSize: 14, color: '#111827' },
    itemTextBold: { fontSize: 14, color: '#111827', fontWeight: '600' },
    divider: { height: 1, backgroundColor: 'rgba(0,0,0,0.06)', width: '100%' },
    badge: {
        backgroundColor: '#EF4444',
        paddingHorizontal: 8,
        height: 20,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
    },
    badgeText: { color: 'white', fontSize: 12, fontWeight: '700' },
    sessionBadges: { flexDirection: 'row', gap: 6 },
    sessionBadge: { paddingHorizontal: 8, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
    upcoming: { backgroundColor: '#3B82F6' },
    ongoing: { backgroundColor: '#10B981' },
});

interface MenuItemProps {
    text: string;
    onPress?: () => void;
    hasWeight?: boolean;
    hasBottomMargin?: boolean;
    count?: number;
    sessionCounts?: {
        upcoming: number;
        ongoing: number;
        past: number;
    };
}

/**
 * The component that make up the account dropdown menu.
 * @param {Object} props - The component props
 * @param {string} props.text - The name of the menu item
 * @param {() => void} [props.onPress] - Optional callback that directs to the page when the item is pressed
 * @param {boolean} [props.hasWeight] - If true, applies bold (500) font weight to the text
 * @param {boolean} [props.hasBottomMargin] - If true, adds bottom margin to the menu item
 */
const MenuItem = ({text, onPress, hasWeight, hasBottomMargin, count, sessionCounts}: MenuItemProps) => {
    const getIconName = () => {
        const key = text.toLowerCase();
        if (key.includes('message')) return 'message-text-outline';
        if (key.includes('notification')) return 'bell-outline';
        if (key.includes('session')) return 'calendar-month-outline';
        if (key.includes('favorite') || key.includes('wishlist')) return 'heart-outline';
        if (key.includes('manage your camp')) return 'home-account';
        if (key.includes('profile')) return 'account-circle-outline';
        if (key.includes('settings')) return 'cog-outline';
        if (key.includes('log out')) return 'logout-variant';
        if (key.includes('log in')) return 'login-variant';
        return 'chevron-right';
    };

    return (
        <Pressable
            onPress={onPress}
            style={({ pressed }) => ([
                styles.itemRow,
                pressed ? { backgroundColor: 'rgba(17,24,39,0.06)' } : null,
                hasBottomMargin ? { marginBottom: 2 } : null,
            ])}
        >
            <View style={styles.left}>
                <View style={styles.iconWrap}>
                    <MaterialCommunityIcons name={getIconName()} size={18} color="#6B7280" />
                </View>
                <Text style={hasWeight ? styles.itemTextBold : styles.itemText}>{text}</Text>
            </View>

            {count !== undefined && count > 0 && (
                <View style={styles.badge}>
                    <Text style={styles.badgeText}>{count > 99 ? '99+' : count}</Text>
                </View>
            )}

            {sessionCounts && (sessionCounts.upcoming > 0 || sessionCounts.ongoing > 0) && (
                <View style={styles.sessionBadges}>
                    {sessionCounts.upcoming > 0 && (
                        <View style={[styles.sessionBadge, styles.upcoming]}>
                            <Text style={styles.badgeText}>{sessionCounts.upcoming}</Text>
                        </View>
                    )}
                    {sessionCounts.ongoing > 0 && (
                        <View style={[styles.sessionBadge, styles.ongoing]}>
                            <Text style={styles.badgeText}>{sessionCounts.ongoing}</Text>
                        </View>
                    )}
                </View>
            )}
        </Pressable>
    );
};

interface AccountDropDownProps {
    isCampPage: boolean;
    setShowDropdown: (showDropdown: boolean) => void;
}

/**
 * The account dropdown menu that appears on all screens/pages.
 * @param {Object} props - The component props
 * @param {boolean} props.isCampPage - The side margins are wider on camp pages
 * @param {() => void} props.setShowDropdown - Whether to display the dropdown menu
 */
const AccountDropDown = ({isCampPage, setShowDropdown}: AccountDropDownProps) => {
    const {setUser: setUser, padding: padding, notifications, user, setShowLoginModal} = useContext(GlobalStateContext);
    
    // Count unread notifications
    const unreadCount = notifications?.filter(notification => !notification.read).length || 0;
    
    // State for session counts
    const [sessionCounts, setSessionCounts] = useState({
        upcoming: 0,
        ongoing: 0,
        past: 0
    });

    // Recently used accounts (stored locally for quick switch)
    const [recentAccounts, setRecentAccounts] = useState<Array<{ userId: string; firstName?: string; lastName?: string; accountType?: string }>>([]);

    // Load session counts across account types
    useEffect(() => {
        const loadSessionCounts = async () => {
            if (!user?.userId) return;

            const toDate = (val: any): Date | null => {
                if (!val) return null;
                try {
                    if (val?.toDate) {
                        const d = val.toDate();
                        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
                    }
                    if (val instanceof Date) return !isNaN(val.getTime()) ? val : null;
                    const d = new Date(val);
                    return !isNaN(d.getTime()) ? d : null;
                } catch { return null; }
            };

            let upcomingCount = 0;
            let ongoingCount = 0;
            let pastCount = 0;

            const classify = (session: any) => {
                if (!session || session.isHidden === true || session.deleted === true) return;
                const start = toDate(session.startAt);
                const end = toDate(session.endAt);
                if (!start || !end) return;
                const now = new Date();
                if (end < now) pastCount++;
                else if (start > now) upcomingCount++;
                else ongoingCount++;
            };

            try {
                if (user.accountType === 'host') {
                    const campIds: string[] = Array.isArray((user as any).camps) ? (user as any).camps : [];
                    for (const campId of campIds) {
                        try {
                            const sessionsMap = await DataOps.fetchSessionsForCamp(campId);
                            Object.values(sessionsMap || {}).forEach(classify);
                        } catch {}
                    }
                } else if (user.accountType === 'coach') {
                    try {
                        const list = await DataOps.fetchSessionsByCoach(user.userId);
                        (list || []).forEach(classify);
                    } catch {}
                } else {
                    // family/other: from registration table
                    try {
                        // Use the same helper as sessions page
                        // We added fetchActiveRegistrationsByUser in DataOps
                        const regs = await DataOps.fetchActiveRegistrationsByUser(user.userId);
                        const ids = Array.from(new Set((regs || []).map((r: any) => r.sessionId).filter(Boolean)));
                        for (const sid of ids) {
                            try { classify(await DataOps.fetchDataFromFirebase('session', sid)); } catch {}
                        }
                    } catch {}
                }

                setSessionCounts({ upcoming: upcomingCount, ongoing: ongoingCount, past: pastCount });
            } catch (error) {
                console.error('Error loading session counts:', error);
            }
        };

        loadSessionCounts();
    }, [user?.userId, user?.accountType]);

    // Load MRU recent accounts from storage
    useEffect(() => {
        try {
            const stored = localStorage.getItem('recentAccounts');
            const list = stored ? JSON.parse(stored) : [];
            if (Array.isArray(list)) {
                setRecentAccounts(list);
            }
        } catch (e) {
            // ignore
        }
    }, []);

    const navigation = useNavigate();
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef, setShowDropdown]);

    const navigateAndClose = (path) => {
        if (user) {
            navigation(path);
            setShowDropdown(false);
        } else {
            setShowDropdown(false);
            setShowLoginModal(true);
        }
    };

    const switchToAccount = async (targetUserId: string, targetType?: 'family' | 'host' | 'coach') => {
        try {
            let remoteUser: any | null = null;

            // If a target type is provided, try that collection first
            if (targetType) {
                try {
                    const collection = DataOps.getCollectionNameForAccountType(targetType);
                    const direct = await DataOps.fetchDataFromFirebase(collection, targetUserId);
                    if (direct) {
                        remoteUser = direct;
                    }
                } catch {}
            }

            // Fallback to best-effort search across collections
            if (!remoteUser) {
                const result = await DataOps.fetchUserFromAnyCollection(targetUserId);
                remoteUser = result?.user || null;
            }

            if (remoteUser) {
                setUser(remoteUser);
                await DataOps.saveDataToStorage('user', remoteUser);
                setShowDropdown(false);
                try { window.location.reload(); } catch {}
            }
        } catch (err) {
            console.error('Failed to switch account', err);
        }
    };

    const getSessionsText = () => {
        switch (user?.accountType) {
            case 'host':
                return 'Camp Sessions';
            case 'coach':
                return 'My Sessions';
            case 'family':
            default:
                return 'My Sessions';
        }
    };

    const getFavoritesText = () => {
        switch (user?.accountType) {
            case 'host':
                return 'Favorites';
            case 'coach':
                return 'Favorites';
            case 'family':
            default:
                return 'Wishlists';
        }
    };

    const getManageAccountText = () => {
        switch (user?.accountType) {
            case 'host':
                return 'Host Profile';
            case 'coach':
                return 'Personal Profile';
            case 'family':
            default:
                return 'Family Profile';
        }
    };

    return (
        <View 
            style={[
                styles.dropdown,
                {right: isCampPage ? padding : 100}
            ]} 
            ref={dropdownRef}
        >
            <Text style={styles.sectionHeader}>Your Activity</Text>
            <MenuItem text="Messages" hasWeight onPress={() => navigateAndClose('/messages')} />
            <MenuItem text="Notifications" hasWeight count={unreadCount} onPress={() => navigateAndClose('/notifications')} />
            <MenuItem text={getSessionsText()} hasWeight sessionCounts={sessionCounts} onPress={() => navigateAndClose('/sessions')} />
            <MenuItem text={getFavoritesText()} hasWeight hasBottomMargin onPress={() => navigateAndClose('/favorites')} />
            <View style={styles.divider} />
            <Text style={styles.sectionHeader}>Account</Text>
            {/* Quick Switch (MRU) */}
            {user && recentAccounts.filter(a => a.userId !== user.userId).slice(0, 3).map((acct, idx) => (
                <MenuItem
                    key={`${acct.userId}-${idx}`}
                    text={`Switch to ${[acct.firstName, acct.lastName].filter(Boolean).join(' ') || acct.userId}${acct.accountType ? ` (${acct.accountType})` : ''}`}
                    onPress={() => switchToAccount(acct.userId, (acct.accountType as any))}
                />
            ))}
            {user?.accountType === 'host' && (
                <MenuItem text="Manage your camp" onPress={() => navigateAndClose('/hosting-main')} />
            )}
            <MenuItem text={getManageAccountText()} onPress={() => navigateAndClose('/account')} />
            <MenuItem text="Settings" hasBottomMargin onPress={() => navigateAndClose('/account/settings')} />
            <View style={styles.divider} />
            {user && (
                <MenuItem 
                    text={'Switch account'}
                    hasWeight
                    onPress={() => {
                        // Open login modal without logging out first
                        setShowDropdown(false);
                        setShowLoginModal(true);
                    }}
                />
            )}
            <MenuItem 
                text={user ? 'Log Out' : 'Log In'}
                hasWeight
                hasBottomMargin
                onPress={
                    async () => {
                        if (user) {
                            setUser(null);
                            await DataOps.saveDataToStorage('user', null);
                            resetTelemetry();
                            setShowDropdown(false);
                        } else {
                            setShowDropdown(false);
                            setShowLoginModal(true);
                        }
                    }
                }
            />
        </View>
    );
};

export default AccountDropDown;